import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useAccessLevel } from '../../hooks/useAccessLevel';

/**
 * Niveles de protección de rutas
 */
export const ProtectionLevel = {
  /** Ruta pública - sin restricciones */
  PUBLIC: 'public',
  /** Requiere wallet conectada - acceso limitado permitido */
  WALLET_REQUIRED: 'wallet_required',
  /** Requiere wallet conectada - acceso limitado permitido (puede ver pero no interactuar) */
  LIMITED_ACCESS: 'limited_access',
  /** Requiere wallet + NFT - acceso completo */
  FULL_ACCESS: 'full_access',
};

/**
 * Componente de protección de rutas
 * 
 * Maneja diferentes niveles de acceso:
 * - PUBLIC: Sin restricciones
 * - WALLET_REQUIRED: Requiere wallet conectada (permite acceso limitado)
 * - LIMITED_ACCESS: Requiere wallet conectada (permite acceso limitado, igual que WALLET_REQUIRED)
 * - FULL_ACCESS: Requiere wallet + NFT
 * 
 * @param {Object} props
 * @param {string} props.level - Nivel de protección requerido
 * @param {string} props.redirectTo - Ruta de redirección si no cumple requisitos (opcional)
 */
export const ProtectedRoute = ({ level = ProtectionLevel.PUBLIC, redirectTo = null }) => {
  const location = useLocation();
  const { isConnected, address } = useWallet();
  const { isLoading, isFullAccess } = useAccessLevel();
  
  // Rutas públicas siempre permitidas
  if (level === ProtectionLevel.PUBLIC) {
    return <Outlet />;
  }
  
  // Mostrar loading mientras se verifica el acceso
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '16px',
          color: '#666',
        }}
      >
        Loading...
      </div>
    );
  }
  
  // WALLET_REQUIRED o LIMITED_ACCESS: Requiere wallet conectada
  if (level === ProtectionLevel.WALLET_REQUIRED || level === ProtectionLevel.LIMITED_ACCESS) {
    if (!isConnected || !address) {
      // Redirigir a home si no está conectado
      return <Navigate to="/" replace state={{ from: location }} />;
    }
    
    // Si tiene wallet conectada, permitir acceso (incluso si es limitado)
    return <Outlet />;
  }
  
  // FULL_ACCESS: Requiere wallet + NFT
  if (level === ProtectionLevel.FULL_ACCESS) {
    if (!isConnected || !address) {
      // Redirigir a home si no está conectado
      return <Navigate to="/" replace state={{ from: location }} />;
    }
    
    if (!isFullAccess) {
      // Si no tiene NFT, redirigir a página de compra NFT
      // o a la ruta especificada en redirectTo
      const targetRoute = redirectTo || '/nft/select-nft-collection';
      return (
        <Navigate
          to={targetRoute}
          replace
          state={{
            from: location,
            message: 'You must purchase a Premium NFT ($30) to access this page.',
          }}
        />
      );
    }
    
    // Tiene wallet + NFT, permitir acceso
    return <Outlet />;
  }
  
  // Por defecto, permitir acceso
  return <Outlet />;
};

/**
 * Helper para obtener el nivel de protección según la ruta
 * @param {string} pathname - Ruta actual
 * @returns {string} Nivel de protección
 */
export const getProtectionLevel = (pathname) => {
  // Rutas públicas
  const publicRoutes = ['/', '/trading', '/login', '/page-register', '/forgot-password', '/reset-password'];
  if (publicRoutes.includes(pathname)) {
    return ProtectionLevel.PUBLIC;
  }
  
  // Rutas de registro/compra NFT - requieren wallet pero permiten acceso limitado
  const nftRoutes = [
    '/register',
    '/nft/register',
    '/nft/select-nft-collection',
    '/nft/choose-character',
    '/nft/buy-pet',
    '/nft/pet-confirmation',
  ];
  if (nftRoutes.some(route => pathname.startsWith(route))) {
    return ProtectionLevel.WALLET_REQUIRED;
  }
  
  // Rutas que requieren acceso completo (NFT)
  const fullAccessRoutes = [
    '/accounts',
    '/accounts/purchase',
    '/operations',
    '/prop-dashboard',
    '/propfirm',
  ];
  if (fullAccessRoutes.some(route => pathname.startsWith(route))) {
    return ProtectionLevel.FULL_ACCESS;
  }
  
  // Por defecto, permitir acceso con wallet (acceso limitado permitido)
  return ProtectionLevel.WALLET_REQUIRED;
};

export default ProtectedRoute;
